import { useCallback, useEffect, useState } from "react";
import { EditGroupProps, groupForm, IEditGroupForm } from "./EditGroup.types";
import { FormBuilderType, useAlert, useAppDispatch } from "@grassrootly/rn-core";
import useBaseRequest from "../../../api/BaseRequest";
import { editGroupActions } from "../../../api/edit-group/EditGroupReducer";
import { GenerateJoinCode, IGroup, UpdateGroup } from "@grassrootly/api";
import { groupsActions } from "../../../api/groups/GroupsReducer";
import { delay } from '../../../components/utils';
import { ImageResponse } from "../../../components/image-picker";
import { Clipboard } from "react-native";

export function useEditGroup({ group, onEditFinished, navigation }: EditGroupProps) {
  const [profileFormState, setProfileForm] = useState<FormBuilderType<IEditGroupForm> | null>(null);
  const [newImage, setNewImage] = useState<ImageResponse | null>(null);
  const dispatch = useAppDispatch();
  const { success } = useAlert();
  const id = group.id;
  const [hideForm, setHideForm] = useState(false);

  useEffect(() => {
    setProfileForm({
      ...groupForm,
      name: { ...groupForm.name, defaultValue: group.name },
      join_code: { ...groupForm.join_code, defaultValue: group.join_code || "" },
    });
  }, [group]);

  const { execute, loading: updating } = useBaseRequest(UpdateGroup, {
    onCompleted: (data) => {
      if (!data) return;
      const updatedGroup = { ...group, ...data };
      dispatch(editGroupActions.setEditGroup(updatedGroup));
      dispatch(groupsActions.updateGroup(updatedGroup));
      setNewImage(null); // Clear the new image state after successful upload
      onEditFinished();
      success({ message: "Group data updated successfully" });
      navigation?.navigate("group", { id });
    },
  });

  const { execute: generateJoinCode, loading: generatingJoinCode } = useBaseRequest(async () => {
    setHideForm(true);
    return await GenerateJoinCode({ id: group.id });
  }, {
    onCompleted: async (g: IGroup) => {
      const updatedGroup = { ...group, ...g };
      dispatch(editGroupActions.setEditGroup(updatedGroup));
      dispatch(groupsActions.updateGroup(updatedGroup));
      // onEditFinished();
      success({ message: "Group join code generated successfully" });
      await delay(500);
      setHideForm(false);
      // navigation?.navigate("group", { id });
    },
  });

  const onSubmitEdit = useCallback(
    (payload: IEditGroupForm) => {
      const frm = new FormData();
      frm.append("name", payload.name);
      frm.append("join_code", payload.join_code);
      if (newImage) {
        frm.append(
          "image",
          JSON.stringify({
            ...newImage,
            name: newImage.name || `group_profile.jpg`,
          })
        );
      }
      execute({ id: id, paylod: frm });
    },
    [group, newImage]
  );

  const copyJoinCode = useCallback(() => {
    if (group.join_code) {
      Clipboard.setString(group.join_code);
      success({ message: "Join code copied to clipboard" });
    }
  }, [group.join_code, success]);

  return {
    profileFormState,
    onSubmitEdit,
    updating,
    generateJoinCode,
    hideForm,
    onNewImagePicked: setNewImage,
    newImage,
    onUploadCancel: () => setNewImage(null),
    copyJoinCode
  };
}
